import { Injectable } from '@angular/core';
import { AngularFirestore, CollectionReference, DocumentReference } from '@angular/fire/compat/firestore';
import { serverTimestamp } from '@angular/fire/firestore';
import { Timestamp } from 'firebase/firestore';

export interface STPData {
  id?: string;
  customerId: string;
  apmData: {
    beneficiaryBank: string;
    beneficiaryName: string;
    beneficiaryNumber: string;
    beneficiaryType: string;
  };
  billingAddress: {
    country: 'MX';
    email: string;
  };
  bankName: string;
  upo: any;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

export interface NuveiBank {
  Bank: string;
  Code: string;
}

@Injectable({
  providedIn: 'root',
})
export class SpeiService {
  private collection: CollectionReference<STPData>;

  constructor(private firestore: AngularFirestore) {
    this.collection = this.firestore.collection<STPData>('spei').ref;
  }

  async getAll(): Promise<STPData[]> {
    const snap = await this.collection.get();
    const data = snap.docs.map((o) => {
      return { id: o.id, ...o.data() };
    });
    return data;
  }
  async getById(uid: string): Promise<STPData | null> {
    const snap = await this.collection.doc(uid).get();
    if (snap.exists) {
      const data = { id: snap.id, ...snap.data() } as STPData;

      return data;
    } else {
      return null;
    }
  }
  async getByCustomerId(customerId: string): Promise<STPData[]> {
    const snap = await this.collection
      .where('customerId', '==', customerId)
      .where('isActive', '==', true)
      .limit(50)
      .orderBy('createdAt', 'desc')
      .get();
    if (snap.size) {
      return snap.docs.map((d) => {
        const data = { id: d.id, ...d.data() } as STPData;
        return data;
      });
    } else {
      return [];
    }
  }

  /*   async getSTPData(customerId: string): Promise<any[]> {
    const ref = this.firestore.collection('customers').doc(customerId).ref;
    const snap = await this.collection
      .where('customerId', '==', ref)
      .where('status', '==', 'success')
      .limit(60)
      .orderBy('createdAt', 'desc')
      .get();

    const STPDatas = await Promise.all(
      snap.docs.map(async (doc) => {
        const STPDataData = doc.data() as STPData;

        const customerSnap = await this.firestore.doc(STPDataData.customerId.path).get().toPromise();
        const customerData = customerSnap?.data() ?? null;

        return {
          uid: doc.id,
          ...STPDataData,
          customer: customerData,
        };
      })
    );

    return STPDatas;
  } */

  async getSTPData(customerId: string): Promise<STPData[]> {
    const snap = await this.collection.where('customerId', '==', customerId).where('isActive', '==', true).get();

    return snap.docs.map((doc) => {
      const STPDataData = doc.data() as STPData;

      return {
        id: doc.id,
        ...STPDataData,
      } as STPData;
    });
  }

  // Crear una nueva transacción
  async create(item: STPData): Promise<DocumentReference<STPData>> {
    return await this.collection.add({
      ...item,
      createdAt: serverTimestamp() as any,
    });
  }

  // Actualizar un elemento
  async update(uid: string, item: Partial<STPData>): Promise<void> {
    await this.collection.doc(uid).update({
      ...item,
      updatedAt: serverTimestamp() as any,
    });
  }

  // Eliminar un elemento
  async delete(uid: string): Promise<void> {
    await this.collection.doc(uid).delete();
  }

  getBanks(): NuveiBank[] {
    return [
      { Bank: 'BANXICO', Code: '2001' },
      { Bank: 'BANCOMEXT', Code: '37006' },
      { Bank: 'BANOBRAS', Code: '37009' },
      { Bank: 'BANJERCITO', Code: '37019' },
      { Bank: 'NAFIN', Code: '37135' },
      { Bank: 'BaBien', Code: '37166' },
      { Bank: 'HIPOTECARIA FED', Code: '37168' },
      { Bank: 'BANAMEX', Code: '40002' },
      { Bank: 'BBVA MEXICO', Code: '40012' },
      { Bank: 'SANTANDER', Code: '40014' },
      { Bank: 'HSBC', Code: '40021' },
      { Bank: 'BAJIO', Code: '40030' },
      { Bank: 'INBURSA', Code: '40036' },
      { Bank: 'MIFEL', Code: '40042' },
      { Bank: 'SCOTIABANK', Code: '40044' },
      { Bank: 'BANREGIO', Code: '40058' },
      { Bank: 'INVEX', Code: '40059' },
      { Bank: 'BANSI', Code: '40060' },
      { Bank: 'AFIRME', Code: '40062' },
      { Bank: 'BANORTE', Code: '40072' },
      { Bank: 'BANK OF AMERICA', Code: '40106' },
      { Bank: 'MUFG', Code: '40108' },
      { Bank: 'JP MORGAN', Code: '40110' },
      { Bank: 'BMONEX', Code: '40112' },
      { Bank: 'VE POR MAS', Code: '40113' },
      { Bank: 'AZTECA', Code: '40127' },
      { Bank: 'AUTOFIN', Code: '40128' },
      { Bank: 'BARCLAYS', Code: '40129' },
      { Bank: 'COMPARTAMOS', Code: '40130' },
      { Bank: 'MULTIVA BANCO', Code: '40132' },
      { Bank: 'ACTINVER', Code: '40133' },
      { Bank: 'INTERCAM BANCO', Code: '40136' },
      { Bank: 'BANCOPPEL', Code: '40137' },
      { Bank: 'ABC CAPITAL', Code: '40138' },
      { Bank: 'CONSUBANCO', Code: '40140' },
      { Bank: 'VOLKSWAGEN', Code: '40141' },
      { Bank: 'CIBANCO', Code: '40143' },
      { Bank: 'BBASE', Code: '40145' },
      { Bank: 'BANKAOOL', Code: '40147' },
      { Bank: 'PAGATODO.', Code: '40148' },
      { Bank: 'INMOBILIARIO', Code: '40150' },
      { Bank: 'DONDE.', Code: '40151' },
      { Bank: 'BANCREA', Code: '40152' },
      { Bank: 'BANCO COVALTO', Code: '40154' },
      { Bank: 'ICBC', Code: '40155' },
      { Bank: 'SABADELL', Code: '40156' },
      { Bank: 'SHINHAN', Code: '40157' },
      { Bank: 'MIZUHO BANK', Code: '40158' },
      { Bank: 'BANK OF CHINA', Code: '40159' },
      { Bank: 'BANCO S3', Code: '40160' },
      { Bank: 'MONEXCB', Code: '90600' },
      { Bank: 'GBM', Code: '90601' },
      { Bank: 'MASARI', Code: '90602' },
      { Bank: 'VALUE', Code: '90605' },
      { Bank: 'VECTOR', Code: '90608' },
      { Bank: 'MULTIVA CBOLSA', Code: '90613' },
      { Bank: 'FINAMEX', Code: '90616' },
      { Bank: 'VALMEX', Code: '90617' },
      { Bank: 'PROFUTURO', Code: '90620' },
      { Bank: 'CB INTERCAM', Code: '90630' },
      { Bank: 'CI BOLSA', Code: '90631' },
      { Bank: 'FINCOMUN', Code: '90634' },
      { Bank: 'NU MEXICO', Code: '90638' },
      { Bank: 'REFORMA', Code: '90642' },
      { Bank: 'STP', Code: '90646' },
      { Bank: 'TACTIV CB', Code: '90648' },
      { Bank: 'CREDICAPITAL', Code: '90652' },
      { Bank: 'KUSPIT', Code: '90653' },
      { Bank: 'UNAGRA', Code: '90656' },
      { Bank: 'ASP INTEGRA OPC', Code: '90659' },
      { Bank: 'ALTERNATIVOS', Code: '90661' },
      { Bank: 'LIBERTAD', Code: '90670' },
      { Bank: 'CAJA POP MEXICA', Code: '90677' },
      { Bank: 'CRISTOBAL COLON', Code: '90680' },
      { Bank: 'CAJA TELEFONIST', Code: '90683' },
      { Bank: 'TRANSFER', Code: '90684' },
      { Bank: 'FONDO (FIRA)', Code: '90685' },
      { Bank: 'INVERCAP', Code: '90686' },
      { Bank: 'FOMPED', Code: '90689' },
      { Bank: 'FONDEADORA', Code: '90699' },
      { Bank: 'TESORED', Code: '90703' },
      { Bank: 'ARCUS', Code: '90706' },
      { Bank: 'NVIO', Code: '90710' },
      { Bank: 'CUENCA', Code: '90723' },
      { Bank: 'SPIN BY OXXO', Code: '90728' },
      { Bank: 'INDEVAL', Code: '90902' },
      { Bank: 'CoDi Valida', Code: '90903' },
      { Bank: 'SANTANDER2*', Code: '90814' },
      { Bank: 'BANAMEX2', Code: '91802' },
      { Bank: 'BBVA BANCOMER2*', Code: '91812' },
      { Bank: 'SANTANDER2*', Code: '91814' },
      { Bank: 'HSBC2*', Code: '91821' },
      { Bank: 'BANORTE2*', Code: '91872' },
      { Bank: 'AZTECA2*', Code: '91927' },
    ];
  }
}
